import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles, ShieldCheck, Building2, ArrowRight, Landmark, Phone, X } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { api, formatError } from '../lib/api';
import { toast } from 'sonner';

export default function AgencyPricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busyPlan, setBusyPlan] = useState(null);
  const [inquiryFor, setInquiryFor] = useState(null); // plan object

  useEffect(() => {
    document.title = 'Agency, Strata & Government plans — Real Tradies B2B';
    api.agencyPlans().then((r) => setPlans(r.data));
    if (user?.role === 'agent') {
      api.mySubscription().then((r) => setCurrent(r.data));
    }
  }, [user]);

  const startTrial = async (plan) => {
    if (!user) {
      toast.info('Register or sign in as a real estate / strata agency to start.');
      return;
    }
    if (user.role !== 'agent') {
      toast.error('Agency plans are only available to real estate / strata accounts.');
      return;
    }
    setBusyPlan(plan.id);
    try {
      const { data } = await api.createCheckout(plan.stripe_lookup_key);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatError(err));
      setBusyPlan(null);
    }
  };

  const [self, entry] = [plans.filter((p) => !p.contact_sales), plans.filter((p) => p.contact_sales)];

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="agency-pricing">
      <Header />

      <section className="border-b border-[#E5E4E0] bg-white">
        <div className="mx-auto max-w-5xl px-6 py-16 md:px-10 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 border border-[#D96C4E] bg-[#D96C4E]/[0.06] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#D96C4E]">
            <Building2 size={12} /> Real estate · Strata · Government
          </div>
          <h1 className="mx-auto mt-6 max-w-3xl font-heading text-4xl font-bold leading-[1.05] tracking-tight text-[#1C1B1A] md:text-6xl lg:text-7xl">
            Every tradie you engage — <span className="italic text-[#1A362D]">verified live</span>, on one dashboard.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-[#5C5B5A]">
            From boutique real estate offices to WA state departments — Real Tradies turns compliance chaos into a
            live ledger anyone in your team can screenshot for insurers, boards or auditors.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3 text-xs font-semibold uppercase tracking-[0.15em] text-[#1A362D]">
            <span className="inline-flex items-center gap-1 rounded-full border border-[#1A362D] bg-[#1A362D]/[0.05] px-3 py-1.5"><ShieldCheck size={12} /> Live insurance status</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#1A362D] bg-[#1A362D]/[0.05] px-3 py-1.5">Audit-ready exports</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#1A362D] bg-[#1A362D]/[0.05] px-3 py-1.5">Strata panel sharing</span>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#1A362D] bg-[#1A362D]/[0.05] px-3 py-1.5"><Landmark size={12} /> Government procurement</span>
          </div>
        </div>
      </section>

      {/* Self-service tiers */}
      <section>
        <div className="mx-auto max-w-6xl px-6 py-16 md:px-10">
          <div className="mb-8 text-center">
            <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#8A8988]">Self-serve · start today</div>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-[#1C1B1A] md:text-4xl">
              For real estate & boutique strata offices
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="plan-grid">
            {self.map((p) => {
              const isCurrent = current && current.plan_id === p.id && current.status !== 'none';
              return (
                <div
                  key={p.id}
                  data-testid={`plan-card-${p.id}`}
                  className={`relative border p-8 md:p-10 ${
                    p.highlight
                      ? 'border-[#1A362D] bg-[#1A362D] text-white shadow-2xl'
                      : 'border-[#1C1B1A] bg-white text-[#1C1B1A]'
                  }`}
                >
                  {p.highlight && (
                    <div className="absolute -top-3 left-8 inline-flex items-center gap-1 bg-[#D96C4E] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
                      <Sparkles size={11} /> Most popular
                    </div>
                  )}
                  <div className={`text-[10px] font-semibold uppercase tracking-[0.25em] ${p.highlight ? 'text-white/70' : 'text-[#8A8988]'}`}>
                    {p.tagline}
                  </div>
                  <div className="mt-4 font-heading text-3xl font-bold md:text-4xl">{p.name}</div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-heading text-5xl font-bold md:text-6xl">${p.price_aud}</span>
                    <span className={`text-sm ${p.highlight ? 'text-white/70' : 'text-[#8A8988]'}`}>/{p.interval} AUD</span>
                  </div>
                  <ul className="mt-6 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className={`flex items-start gap-2 text-sm ${p.highlight ? 'text-white/90' : 'text-[#5C5B5A]'}`}>
                        <Check size={16} className={p.highlight ? 'mt-0.5 shrink-0 text-white' : 'mt-0.5 shrink-0 text-[#1A362D]'} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <div className={`mt-8 flex items-center justify-center gap-2 border px-4 py-3 text-sm font-semibold uppercase tracking-[0.15em] ${p.highlight ? 'border-white/40 text-white' : 'border-[#2A9D8F] text-[#2A9D8F]'}`}>
                      <Check size={16} /> {current.status === 'trial' ? 'Active (trial)' : 'Active'}
                    </div>
                  ) : (
                    <button
                      onClick={() => startTrial(p)}
                      disabled={busyPlan === p.id}
                      data-testid={`plan-cta-${p.id}`}
                      className={`mt-8 flex w-full items-center justify-center gap-2 border px-4 py-3 text-sm font-semibold uppercase tracking-[0.15em] transition-colors disabled:opacity-50 ${
                        p.highlight
                          ? 'border-white bg-white text-[#1A362D] hover:bg-[#F3F2EE]'
                          : 'border-[#1C1B1A] bg-[#1C1B1A] text-white hover:bg-[#1A362D]'
                      }`}
                    >
                      {busyPlan === p.id ? 'Opening secure checkout...' : `${p.cta} — $${p.price_aud}/mo`} <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enterprise / Government tiers */}
      <section className="border-t border-[#E5E4E0] bg-[#1C1B1A] text-white">
        <div className="mx-auto max-w-6xl px-6 py-16 md:px-10 md:py-20">
          <div className="mb-10 text-center">
            <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#D96C4E]">Enterprise · procurement · government</div>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight md:text-5xl">
              For large strata, councils & WA state departments
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-white/70">
              Custom procurement packs, SSO, API access, SLA-backed uptime and Aboriginal / regional business
              preferencing. We ship the compliance evidence your legal team already asks for.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="enterprise-grid">
            {entry.map((p) => (
              <div key={p.id} data-testid={`plan-card-${p.id}`} className="border border-white/15 bg-white/[0.04] p-8 md:p-10">
                <div className="flex items-center gap-2 text-[#D96C4E]">
                  {p.id === 'government_council' ? <Landmark size={18} /> : <Building2 size={18} />}
                  <div className="text-[10px] font-semibold uppercase tracking-[0.25em]">{p.tagline}</div>
                </div>
                <div className="mt-3 font-heading text-3xl font-bold md:text-4xl">{p.name}</div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-heading text-4xl font-bold md:text-5xl">from ${p.price_aud}</span>
                  <span className="text-sm text-white/60">/mo AUD</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-white/85">
                      <Check size={16} className="mt-0.5 shrink-0 text-[#D96C4E]" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setInquiryFor(p)}
                  data-testid={`enterprise-cta-${p.id}`}
                  className="mt-8 flex w-full items-center justify-center gap-2 border border-[#D96C4E] bg-[#D96C4E] px-4 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#C25A3D]"
                >
                  {p.cta} <ArrowRight size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fine print / trust */}
      <section>
        <div className="mx-auto max-w-6xl px-6 py-16 md:px-10">
          <div className="border border-dashed border-[#E5E4E0] bg-white p-8" data-testid="pricing-fine-print">
            <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#8A8988]">Who pays for what</div>
            <div className="mt-3 grid grid-cols-1 gap-6 text-sm text-[#5C5B5A] md:grid-cols-3">
              <div><b className="block text-[#1C1B1A]">Homeowners: $0.</b> Browse and call any Perth tradie free forever. Always.</div>
              <div><b className="block text-[#1C1B1A]">Tradies: $0.</b> No pay-per-lead. No pay-to-rank. This is our promise, in writing.</div>
              <div><b className="block text-[#1C1B1A]">Agencies & govt: paid.</b> Because that's who has revenue and risk on the line, and who benefits most from live compliance.</div>
            </div>
          </div>

          {!user && (
            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border border-[#1C1B1A] bg-white p-6">
              <div>
                <div className="font-heading text-xl font-bold text-[#1C1B1A]">Run an agency in Perth?</div>
                <div className="mt-1 text-sm text-[#5C5B5A]">Create your agency account in 60 seconds and start your free trial.</div>
              </div>
              <Link to="/register" className="btn-primary" data-testid="pricing-register-agency">
                Create agency account <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>
      </section>

      {inquiryFor && <EnterpriseInquiryModal plan={inquiryFor} onClose={() => setInquiryFor(null)} />}
    </div>
  );
}


function EnterpriseInquiryModal({ plan, onClose }) {
  const [form, setForm] = useState({
    org_name: '',
    contact_name: '',
    email: '',
    phone: '',
    org_type: plan.id === 'government_council' ? 'government' : 'strata',
    properties_or_scope: '',
    message: '',
  });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.enterpriseInquiry({ ...form, plan_id: plan.id });
      setOk(true);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="enterprise-modal">
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-lg border border-[#1C1B1A] bg-white p-8">
        <button onClick={onClose} className="absolute right-4 top-4 text-[#8A8988] hover:text-[#1C1B1A]" data-testid="enterprise-modal-close"><X size={20} /></button>
        {ok ? (
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#1A362D] text-white">
              <Check size={30} />
            </div>
            <h3 className="mt-6 font-heading text-2xl font-bold text-[#1C1B1A]">Thanks — we'll be in touch.</h3>
            <p className="mt-2 text-sm text-[#5C5B5A]">Our procurement liaison will send your pack within 1 business day. Emails come from procurement@realtradies.com.au.</p>
            <button onClick={onClose} className="btn-primary mt-6">Close</button>
          </div>
        ) : (
          <>
            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#D96C4E]">{plan.name}</div>
            <h3 className="mt-2 font-heading text-2xl font-bold text-[#1C1B1A]">Request the procurement pack</h3>
            <p className="mt-2 text-sm text-[#5C5B5A]">Includes pricing, SLA, security, DTA/DMIRS alignment and audit-trail sample.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="enterprise-form">
              <input required value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} placeholder="Organisation name" className="input-editorial" data-testid="enterprise-org" />
              <div className="grid grid-cols-2 gap-3">
                <input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Your name" className="input-editorial" data-testid="enterprise-contact" />
                <select value={form.org_type} onChange={(e) => setForm({ ...form, org_type: e.target.value })} className="input-editorial" data-testid="enterprise-type">
                  <option value="strata">Strata firm</option>
                  <option value="government">State government / dept</option>
                  <option value="council">Local council</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Work email" className="input-editorial" data-testid="enterprise-email" />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="input-editorial" data-testid="enterprise-phone" />
              </div>
              <input value={form.properties_or_scope} onChange={(e) => setForm({ ...form, properties_or_scope: e.target.value })} placeholder="Approx. properties / scope (e.g. 600 lots, 12 offices)" className="input-editorial" data-testid="enterprise-scope" />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything specific we should know?" rows={3} className="input-editorial" data-testid="enterprise-message" />
              <button type="submit" disabled={busy} className="btn-primary w-full py-3 disabled:opacity-50" data-testid="enterprise-submit">
                {busy ? 'Sending...' : 'Request procurement pack'} <ArrowRight size={14} />
              </button>
              <p className="text-center text-[10px] uppercase tracking-[0.15em] text-[#8A8988]"><Phone size={10} className="inline" /> Prefer a call? procurement@realtradies.com.au</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
